<template>
  <div class="qr-frame" :style="{ padding: pad + 'px' }">
    <svg
      ref="svg"
      :width="size"
      :height="size"
      :viewBox="`0 0 ${moduleCount} ${moduleCount}`"
      shape-rendering="crispEdges"
      role="img"
      aria-label="QR code"
    >
      <rect :width="moduleCount" :height="moduleCount" fill="#ffffff" />
      <path :d="darkPath" :fill="cellColor" />
      <template v-if="logo">
        <!-- Branded centre mark. The QR is generated with error-correction H,
             which tolerates ~30% damage; the mark covers well under that. -->
        <rect
          :x="logoBox.x" :y="logoBox.y"
          :width="logoBox.w" :height="logoBox.h"
          rx="0.8" fill="#ffffff"
        />
        <rect
          :x="logoBox.x + 0.7" :y="logoBox.y + 0.7"
          :width="logoBox.w - 1.4" :height="logoBox.h - 1.4"
          rx="0.6" :fill="brandColor"
        />
        <rect
          :x="logoBox.cx - 0.85" :y="logoBox.cy - 0.85"
          width="1.7" height="1.7" rx="0.3"
          fill="none" stroke="#ffffff" stroke-width="0.42"
          :transform="`rotate(45 ${logoBox.cx} ${logoBox.cy})`"
        />
      </template>
    </svg>
  </div>
</template>

<script>
// Real, scannable QR code (branded). Encodes `value` — pass the full short URL,
// not just the slug. Replaces the old decorative makeQR() matrix that could
// never scan.
import QRCode from 'qrcode';

const BRAND_FALLBACK = '#c26a2f';

export default {
  name: 'QrCode',
  props: {
    value: { type: String, required: true },
    size: { type: Number, default: 132 },
    pad: { type: Number, default: 14 },
    logo: { type: Boolean, default: true },
    cellColor: { type: String, default: '#0a0a0a' },
  },
  data() {
    return { brandColor: BRAND_FALLBACK };
  },
  computed: {
    matrix() {
      // errorCorrectionLevel H so the centre mark never breaks scanning.
      const qr = QRCode.create(this.value || ' ', { errorCorrectionLevel: 'H' });
      return qr.modules;
    },
    moduleCount() { return this.matrix.size; },
    darkPath() {
      const { size, data } = this.matrix;
      let d = '';
      for (let r = 0; r < size; r++) {
        for (let c = 0; c < size; c++) {
          if (data[r * size + c]) d += `M${c} ${r}h1v1h-1z`;
        }
      }
      return d;
    },
    logoBox() {
      // ~5 modules wide, centred; small enough for ECC H to absorb.
      const n = this.moduleCount;
      const w = Math.min(7, Math.max(5, Math.round(n * 0.18)));
      const x = (n - w) / 2;
      return { x, y: x, w, h: w, cx: n / 2, cy: n / 2 };
    },
  },
  mounted() {
    // Resolve the theme accent for the centre mark. Fixed at mount (not
    // reactive to theme toggles) — acceptable for a decorative mark, and it
    // keeps SVG serialization (downloads) free of CSS variables.
    const accent = getComputedStyle(this.$el).getPropertyValue('--accent').trim();
    if (accent) this.brandColor = accent;
  },
  methods: {
    // Download the QR as a PNG (rendered at high resolution from the SVG).
    download(filename) {
      const svg = this.$refs.svg;
      if (!svg) return;
      const scale = 1024;
      const xml = new XMLSerializer().serializeToString(svg);
      const svgUrl = URL.createObjectURL(new Blob([xml], { type: 'image/svg+xml' }));
      const img = new Image();
      img.onload = () => {
        const margin = Math.round(scale * 0.08); // quiet zone
        const canvas = document.createElement('canvas');
        canvas.width = canvas.height = scale + margin * 2;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(img, margin, margin, scale, scale);
        URL.revokeObjectURL(svgUrl);
        canvas.toBlob((blob) => {
          if (!blob) return;
          const a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = filename || 'qr-code.png';
          a.click();
          URL.revokeObjectURL(a.href);
        }, 'image/png');
      };
      img.onerror = () => URL.revokeObjectURL(svgUrl);
      img.src = svgUrl;
    },
  },
};
</script>

<style scoped>
.qr-frame {
  position: relative;
  background: #fff;
  border-radius: var(--radius);
  border: 1px solid var(--border);
  display: inline-block;
  line-height: 0;
}
</style>
